import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowUpRightFromSquare,
  faBuilding,
  faFileContract,
  faShieldHalved,
} from '@fortawesome/pro-duotone-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';
import { AppService } from '../../core/services/app.service';
import { Tab, Tabs } from '../../shared/components/ui/tabs/tabs';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { Changelog } from './changelog/changelog';
import { Licenses } from './licenses/licenses';

const TAB_IDS = ['about', 'changelog', 'licenses'] as const;
type AboutTab = (typeof TAB_IDS)[number];
const TAB_LABELS: Record<AboutTab, string> = {
  about: _('About'),
  changelog: _('Changelog'),
  licenses: _('Licenses'),
};

const LEGAL_LINKS = [
  {
    label: _('Terms of Service'),
    url: 'https://www.4players.io/company/terms/',
    icon: faFileContract,
  },
  {
    label: _('Privacy Policy'),
    url: 'https://www.4players.io/company/privacy/',
    icon: faShieldHalved,
  },
  { label: _('Imprint'), url: 'https://www.4players.io/company/imprint/', icon: faBuilding },
];

@Component({
  selector: 'andor-about',
  standalone: true,
  imports: [TranslatePipe, FontAwesomeModule, Tabs, Changelog, Licenses],
  templateUrl: './about.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './about.scss',
})
export class About {
  readonly #userSettings = inject(UserSettingsStore);

  protected readonly version = inject(AppService).version;
  protected readonly legalLinks = LEGAL_LINKS;
  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  protected readonly colorMode = toSignal(this.#userSettings.colorMode$, {
    initialValue: this.#userSettings.colorMode,
  });

  protected readonly tabs = TAB_IDS.map((id): Tab => ({ id, label: TAB_LABELS[id] }));
  protected readonly activeId = signal<AboutTab>('about');
  protected readonly activeTab = computed(() =>
    this.tabs.find((tab) => tab.id === this.activeId())
  );

  protected select(tab: Tab | undefined): void {
    const id = TAB_IDS.find((candidate) => candidate === tab?.id);
    if (id) this.activeId.set(id);
  }
}
